import { useReducer, useCallback, useMemo } from 'react';

/**
 * Per-objective state slice for the multi-objective-aware upload modal.
 *
 * Each entry is keyed by `usecase._id`. Tabs in `ReviewOutputsStep` mutate
 * exactly one entry at a time; the active tab is held in shell state.
 *
 * @typedef {Object} OutputEntry
 * @property {boolean} hasData
 * @property {string|null} labelColumn      - Name of the label/entity column.
 * @property {Object} labelMappings         - { extractedLabel: targetLabel }
 * @property {string|null} relationsColumn  - Name of the relations column (NER+rel only).
 * @property {Object} relationsMappings     - { extractedRel: targetRel }
 * @property {string|null} summaryColumn    - Name of the summary column (summarization only).
 * @property {string|null} delimiter        - Per-objective multi-label delimiter.
 * @property {string|null} rejectionLabelName - Single-label "Other" fallback label (post-mapping).
 * @property {Array} extractedLabels        - Cached preview labels.
 * @property {Array} extractedRelations     - Cached preview relations.
 * @property {string|null} extractedSummary - Cached preview summary.
 */

/**
 * @param {Object} subGroup - { sub_group_name, usecases: [...] } or null for legacy single-task.
 */
export const initialPerOutput = (subGroup) => {
    const usecases = subGroup?.usecases || [];
    if (usecases.length === 0) {
        return {
            __default__: makeEntry(),
        };
    }
    return usecases.reduce((acc, uc) => {
        acc[String(uc._id)] = makeEntry();
        return acc;
    }, {});
};

const makeEntry = () => ({
    hasData: true,
    // "Does your data contain labels/entities/summaries/outputs?" — default ON.
    hasLabels: true,
    // When hasLabels is false: 'generate_with_ai' (default) | 'unlabeled'.
    noLabelsMode: 'generate_with_ai',
    labelColumn: null,
    // Retains the originally-picked label column even after the
    // "Does your data contain labels?" toggle is turned OFF. Classification
    // only: original labels can be snapshotted into an `origin_labels` extra column.
    detectedLabelColumn: null,
    labelMappings: {},
    relationsColumn: null,
    relationsMappings: {},
    summaryColumn: null,
    delimiter: null,
    // Single-label classification confidence-threshold fallback ("Fallback" label).
    rejectionLabelName: null,
    // Multilabel classification only: how to handle empty label rows.
    // 'none_selected' -> [""] (default); 'unlabeled' -> []; 'exclude' -> skip row.
    emptyMultilabelHandling: 'none_selected',
    extractedLabels: [],
    extractedRelations: [],
    extractedSummary: null,
    // Rows produced by the "Generate labels with AI" block when
    // `hasLabels === false` and noLabelsMode is generate_with_ai.
    // Shape: see `utils/task/aiLabels.js`.
    aiLabels: [],
});

const normalizeUsecaseId = (usecaseId) =>
    usecaseId == null || usecaseId === '__default__' ? usecaseId : String(usecaseId);

const reducer = (state, action) => {
    const { type, usecaseId } = action;
    if (!usecaseId) return state;
    const id = normalizeUsecaseId(usecaseId);
    const entry = state[id] || makeEntry();
    switch (type) {
        case 'SET_FIELD':
            return { ...state, [id]: { ...entry, [action.field]: action.value } };
        case 'PATCH':
            return { ...state, [id]: { ...entry, ...action.patch } };
        case 'RESET':
            return { ...state, [id]: makeEntry() };
        default:
            return state;
    }
};

/**
 * Multi-objective-aware state hook for the upload modal.
 *
 * In legacy single-task mode (`subGroup === null`), it still works — there's a
 * single `__default__` slot and consumers can ignore the multi-key shape.
 *
 * Returns helpers: `getEntry`, `setField`, `patchEntry`, `resetEntry`, plus
 * the full `perOutput` map for serialization in the final payload.
 */
export const useUploadModalState = (subGroup) => {
    const [perOutput, dispatch] = useReducer(
        reducer,
        subGroup,
        initialPerOutput
    );

    const getEntry = useCallback(
        (usecaseId) =>
            perOutput[
                usecaseId == null || usecaseId === '__default__'
                    ? '__default__'
                    : String(usecaseId)
            ] || makeEntry(),
        [perOutput]
    );

    const setField = useCallback((usecaseId, field, value) => {
        dispatch({
            type: 'SET_FIELD',
            usecaseId: usecaseId == null || usecaseId === '__default__' ? '__default__' : String(usecaseId),
            field,
            value,
        });
    }, []);

    const patchEntry = useCallback((usecaseId, patch) => {
        dispatch({
            type: 'PATCH',
            usecaseId: usecaseId == null || usecaseId === '__default__' ? '__default__' : String(usecaseId),
            patch,
        });
    }, []);

    const resetEntry = useCallback((usecaseId) => {
        dispatch({
            type: 'RESET',
            usecaseId: usecaseId == null || usecaseId === '__default__' ? '__default__' : String(usecaseId),
        });
    }, []);

    const isMultiObjective = useMemo(() => {
        const usecases = subGroup?.usecases || [];
        return (subGroup?.sub_group_name || '').trim().length > 0 && usecases.length > 1;
    }, [subGroup]);

    return {
        perOutput,
        getEntry,
        setField,
        patchEntry,
        resetEntry,
        isMultiObjective,
    };
};
